import React from 'react';
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, X } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useMessagesTranslations } from './hooks/useMessagesTranslations';
import { AspectRatio } from "@/components/ui/aspect-ratio";

type Student = {
  auth_id: string;
  first_name: string;
  last_name: string;
  avatar_url?: string | null;
};

interface StudentSelectionGridProps {
  students: Student[];
  selectedStudents: string[];
  onSelectStudent: (studentId: string) => void;
  onRemoveStudent: (studentId: string) => void;
  loading?: boolean;
}

export const StudentSelectionGrid = ({ 
  students, 
  selectedStudents, 
  onSelectStudent,
  onRemoveStudent,
  loading = false
}: StudentSelectionGridProps) => {
  const { t } = useMessagesTranslations();

  if (loading) {
    return <div className="text-center py-4 dark:text-white">{t('loading')}</div>;
  }
  
  if (students.length === 0) {
    return <div className="text-center py-4 dark:text-white">{t('noStudentsFound')}</div>;
  }
  
  return (
    <div className="space-y-6">
      {/* Selected students display */}
      {selectedStudents.length > 0 && (
        <div className="bg-slate-50 dark:bg-gray-800/80 rounded-lg p-4 border border-slate-200 dark:border-gray-700">
          <h3 className="text-sm font-medium mb-2 dark:text-white">
            {selectedStudents.length} {t('selectedStudents')}
          </h3>
          <ScrollArea className="h-24">
            <div className="flex flex-wrap gap-2">
              {selectedStudents.map((studentId, index) => {
                const student = students.find(s => s.auth_id === studentId);
                if (!student) return null;
                
                return (
                  <div key={studentId} className="flex items-center bg-white dark:bg-gray-700 rounded-xl p-1.5 pr-3 border border-slate-100 dark:border-gray-600 shadow-sm">
                    <div className="w-10 h-10 mr-2 relative">
                      <div className="absolute -top-1 -left-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center text-white text-xs font-bold z-10 shadow-sm">
                        {index + 1}
                      </div>
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={student.avatar_url || ''} />
                        <AvatarFallback>
                          {student.first_name?.charAt(0)?.toUpperCase() || '?'}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                    <span className="text-sm mr-2 max-w-32 truncate dark:text-white">
                      {student.first_name} {student.last_name}
                    </span>
                    <button 
                      type="button" 
                      className="text-slate-400 dark:text-slate-200 hover:text-destructive dark:hover:text-destructive rounded-full hover:bg-slate-100 dark:hover:bg-gray-500 p-1"
                      onClick={(e) => {
                        console.log('[StudentSelectionGrid] X button clicked for student:', student.auth_id);
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('[StudentSelectionGrid] >>> Calling onRemoveStudent (from selected list)');
                        onRemoveStudent(student.auth_id);
                      }}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      )}
      
      {/* Students grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 w-full">
        {students.map((student) => {
          const isSelected = selectedStudents.includes(student.auth_id);
          const selectedIndex = selectedStudents.indexOf(student.auth_id);
          
          return (
            <div 
              key={student.auth_id} 
              className={`bg-white border rounded-xl overflow-hidden hover:shadow-md transition-all
                dark:bg-gray-800 dark:border-gray-700 
                ${isSelected ? 'border-primary border-2 dark:border-primary' : 'border-slate-200 dark:border-gray-700'}`}
            >
              <button
                type="button"
                className="w-full h-full text-left focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800 rounded-xl"
                onClick={(e) => {
                  console.log('[StudentSelectionGrid] Participant grid button clicked for student:', student.auth_id, 'isSelected:', isSelected);
                  e.preventDefault();
                  e.stopPropagation();
                  if (isSelected) {
                    console.log('[StudentSelectionGrid] >>> Calling onRemoveStudent (from grid)');
                    onRemoveStudent(student.auth_id);
                  } else {
                    console.log('[StudentSelectionGrid] >>> Calling onSelectStudent (from grid)');
                    onSelectStudent(student.auth_id);
                  }
                }}
              >
                <div className="relative overflow-hidden group">
                  {isSelected && (
                    <div className="absolute top-2 left-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white font-bold z-10 shadow-sm">
                      {selectedIndex + 1}
                    </div>
                  )}
                  
                  <AspectRatio ratio={1/1} className="bg-slate-50 dark:bg-gray-700">
                    <Avatar className="h-full w-full">
                      <AvatarImage src={student.avatar_url || ''} className="object-cover" />
                      <AvatarFallback className="text-xl">
                        {student.first_name?.charAt(0)?.toUpperCase() || '?'}
                      </AvatarFallback>
                    </Avatar>
                  </AspectRatio>
                  
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant={isSelected ? "destructive" : "secondary"} size="sm" className="gap-1 rounded-full shadow-md">
                      <Check className="h-4 w-4" />
                      {isSelected ? t('unselect') : t('select')}
                    </Button>
                  </div>
                </div>
                
                <div className="p-3 space-y-1">
                  <h3 className="text-xs sm:text-sm font-medium truncate dark:text-white">
                    {student.first_name} {student.last_name}
                  </h3>
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
